import React, { useState, useEffect, useMemo } from 'react'
import { LazyBackground } from './LazyLoading'

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [loadedImages, setLoadedImages] = useState(new Set())
  const [isMobile, setIsMobile] = useState(false)

  // Check if the device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768) // 768px is a common breakpoint for mobile devices
    }
    
    // Initial check
    checkMobile()
    
    // Add event listener for window resize
    window.addEventListener('resize', checkMobile)
    
    // Cleanup
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Carousel content with mobile alternatives
  const slides = useMemo(
    () => [
      {
        image: '/images/main.png',
        mobileImage: '/images/hero-mobile-1.jpg', // Add mobile-specific image
        title: 'Travel with Ease',
        highlight: 'Work with Peace',
        description:
          'Experience Seamless Comfort and Unmatched Convenience in Every Journey. We Prioritize Your Travel, So You Can Focus on What Truly Matters.',
      },
      {
        image: '/images/taxi-7855475_1920.jpg',
        mobileImage: '/images/hero-mobile-2.jpg', // Add mobile-specific image
        title: 'Corporate Travel Solutions',
        highlight: 'Professional Service',
        description:
          'Reliable transportation services tailored for businesses. Ensuring timely and comfortable employee commute.',
      },
    ],
    []
  )

  // Auto-scroll effect
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length)
    }, 5000)

    return () => clearInterval(timer)
  }, [slides.length])

  // Preload next image based on device type
  useEffect(() => {
    const nextSlide = (currentSlide + 1) % slides.length
    if (!loadedImages.has(`${nextSlide}-${isMobile ? 'mobile' : 'desktop'}`)) {
      const img = new Image()
      img.src = isMobile ? slides[nextSlide].mobileImage : slides[nextSlide].image
      img.onload = () => {
        setLoadedImages((prev) => new Set([...prev, `${nextSlide}-${isMobile ? 'mobile' : 'desktop'}`]))
      }
    }
  }, [currentSlide, slides, loadedImages, isMobile])

  return (
    <div
      className="relative min-h-screen overflow-hidden mt-6 h-[90vh] sm:h-[80vh] md:h-[75vh] lg:h-[70vh] xl:h-[65vh]"
      data-aos="fade-down"
      data-aos-duration="500"
    >
      {/* Carousel Slides */}
      {slides.map((slide, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
            currentSlide === index ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {/* Background Image with LazyBackground */}
          <div className="relative h-full overflow-hidden">
            <LazyBackground
              src={isMobile ? slide.mobileImage : slide.image}
              placeholder="/images/placeholder.png"
              className="absolute inset-0 bg-cover bg-center bg-fixed"
              style={{
                transform: currentSlide === index ? 'scale(1)' : 'scale(1.05)',
                transition: 'transform 2000ms',
              }}
            />
            <div className="absolute inset-0 bg-gray-900/70" />
            {/* Content Section */}
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-center h-full pt-20 sm:pt-32 md:pt-40 lg:pt-44 font-domine z-10">
              <div
                className={`text-center text-white transition-all duration-1000 transform ${
                  currentSlide === index
                    ? 'translate-y-0 opacity-100'
                    : 'translate-y-10 opacity-0'
                }`}
              >
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-shadow-lg">
                  {slide.title} <br />
                  <span className="text-[#6ebcff]">{slide.highlight}</span>
                </h1>
                <p className="text-sm sm:text-base md:text-lg lg:text-xl mb-8 text-white max-w-3xl mx-auto text-shadow-md">
                  {slide.description}
                </p>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Slide Indicators */}
      <div className="absolute bottom-8 left-0 right-0 flex justify-center gap-2">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${
              currentSlide === index ? 'bg-white w-8' : 'bg-white/50'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  )
}

export default HeroSection